import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSettings, updateSettings as updateSettingsApi, uploadLogo } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Upload, Save } from 'lucide-react';

const Settings = () => {
  const { updateSettings: updateContextSettings } = useAuth();
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    subtitle: '',
    primary_color: '#0f172a',
    accent_color: '#FF6A13',
    cnpj_construtora: '',
    pix_construtora: ''
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now());
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);
      setFormData({
        company_name: response.data.company_name,
        subtitle: response.data.subtitle || '',
        primary_color: response.data.primary_color || '#0f172a',
        accent_color: response.data.accent_color || '#FF6A13',
        cnpj_construtora: response.data.cnpj_construtora || '',
        pix_construtora: response.data.pix_construtora || ''
      });
      
      // Update context with new settings
      updateContextSettings(response.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await updateSettingsApi(formData);
      
      // Update context with new settings
      updateContextSettings(response.data);
      
      toast.success('Configurações salvas com sucesso!');
      fetchSettings();
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadLogo(file);
      toast.success('Logo enviado com sucesso!');
      
      // Force refresh settings and update timestamp for cache busting
      await fetchSettings();
      setLogoTimestamp(Date.now());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
          Configurações
        </h1>
        <p className="text-slate-600 font-primary mt-1">Gerencie as configurações do sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-sm bg-accent/10">
              <SettingsIcon className="h-5 w-5 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900">
              Informações da Empresa
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="settings-form">
            <div>
              <Label htmlFor="company_name" className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                Nome da Empresa *
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                className="mt-1 rounded-sm"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label htmlFor="subtitle" className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                Subtítulo *
              </Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                required
                className="mt-1 rounded-sm"
                placeholder="Ex: Sistema de Gestão Financeira"
                data-testid="subtitle-input"
              />
            </div>

            {/* Construtora identity — used as "Favorecido" for client_empreita launches */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              <div className="sm:col-span-2">
                <p className="text-xs text-slate-500 mt-2 mb-1">
                  Dados da Construtora (usados como favorecido em lançamentos de Empreita para o Cliente)
                </p>
              </div>
              <div>
                <Label htmlFor="cnpj_construtora" className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  CNPJ da Construtora
                </Label>
                <Input
                  id="cnpj_construtora"
                  value={formData.cnpj_construtora}
                  onChange={(e) => setFormData({ ...formData, cnpj_construtora: e.target.value })}
                  className="mt-1 rounded-sm"
                  placeholder="00.000.000/0000-00"
                  data-testid="cnpj-construtora-input"
                />
              </div>
              <div>
                <Label htmlFor="pix_construtora" className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Chave Pix da Construtora
                </Label>
                <Input
                  id="pix_construtora"
                  value={formData.pix_construtora}
                  onChange={(e) => setFormData({ ...formData, pix_construtora: e.target.value })}
                  className="mt-1 rounded-sm"
                  placeholder="CNPJ, e-mail, telefone ou chave aleatória"
                  data-testid="pix-construtora-input"
                />
              </div>
            </div>
            
            {/* Color Pickers */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <Label htmlFor="primary_color" className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Cor Primária *
                </Label>
                <div className="flex gap-2 mt-1">
                  <input
                    id="primary_color"
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="h-10 w-20 rounded-sm border border-slate-200 cursor-pointer"
                    data-testid="primary-color-input"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="flex-1 rounded-sm"
                    placeholder="#0f172a"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Cor principal do sistema (botões, menu)</p>
              </div>
              <div>
                <Label htmlFor="accent_color" className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Cor de Destaque *
                </Label>
                <div className="flex gap-2 mt-1">
                  <input
                    id="accent_color"
                    type="color"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="h-10 w-20 rounded-sm border border-slate-200 cursor-pointer"
                    data-testid="accent-color-input"
                  />
                  <Input
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="flex-1 rounded-sm"
                    placeholder="#FF6A13"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Cor de ícones e destaques</p>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              data-testid="save-settings-button"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </form>
        </div>

        {/* Logo Upload */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-sm bg-accent/10">
              <Upload className="h-5 w-5 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900">
              Logo da Empresa
            </h2>
          </div>
          <div className="space-y-4">
            {settings?.logo_base64 || settings?.logo_url ? (
              <div className="flex flex-col items-center">
                <div className="w-40 h-40 border-2 border-slate-200 rounded-sm flex items-center justify-center bg-slate-50 mb-4">
                  <img
                    key={settings.logo_base64 || settings.logo_url}
                    src={settings.logo_base64 || `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain"
                    data-testid="current-logo"
                  />
                </div>
                <p className="text-sm text-slate-600 mb-4">Logo atual</p>
              </div>
            ) : (
              <div className="w-40 h-40 mx-auto border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center bg-slate-50 mb-4">
                <p className="text-sm text-slate-500">Sem logo</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="logo-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full bg-accent text-white hover:bg-accent/90 rounded-sm"
              data-testid="upload-logo-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Enviando...' : 'Enviar Nova Logo'}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Formatos aceitos: JPG, PNG, SVG
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
